/**
 * React bindings of the services the Settings page reads (W2-13 settings-keymap).
 *
 * Every hook subscribes to its service, so rows, the keymap editor and the sections re-render live: settings
 * changed elsewhere (another tab, the first-run question, the level chip), a locale switch, keymap changes (preset,
 * Alt-free, overrides, modules attached), skins installed, storage health.
 * Text goes through `services.i18n` (the app's I18n), so a language switch re-renders the whole page at once.
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { AppServices, KeyBinding, KeymapService } from '../../app/registry/types';
import type { UserSettings } from '../../app/settings/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { I18n, MessageKey } from '../../i18n/types';
export { useSettings as useSettingsValue } from '../../app/registry/react';

export interface SettingsText {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
}

/** Translation through `services.i18n`; a new object per locale so memoised children update. */
export function useText(services: Pick<AppServices, 'i18n'>): SettingsText {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(() => textFor(i18n, locale), [i18n, locale]);
}

function textFor(i18n: I18n, locale: Locale): SettingsText {
  return {
    locale,
    t: (key, params) => i18n.t(key, params),
    tr: (ref) => i18n.tr(ref),
  };
}

/**
 * A counter that increases whenever `subscribe` reports a change (for services whose getters return new objects
 * on every call, such as keymap.bindings() of some implementations).
 */
export function useVersion(subscribe: ((cb: () => void) => Unsubscribe) | null): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (subscribe === null) return undefined;
    return subscribe(() => {
      setVersion((v) => v + 1);
    });
  }, [subscribe]);
  return version;
}

interface BindingsStore {
  subscribe(cb: () => void): Unsubscribe;
  get(): readonly KeyBinding[];
}

const bindingStores = new WeakMap<KeymapService, BindingsStore>();

/**
 * A snapshot store over the keymap, shared by every component of the page: the effective bindings keep their
 * identity until the keymap changes, and the page and the keymap editor see the same list (so what is derived from
 * it, such as the conflicts of each binding, is computed once). The store listens to the keymap for as long as the
 * keymap exists, so its snapshot is never stale, not even between a render and the subscription of its effect.
 */
function bindingsStore(keymap: KeymapService): BindingsStore {
  const known = bindingStores.get(keymap);
  if (known !== undefined) return known;
  const listeners = new Set<() => void>();
  let cached: readonly KeyBinding[] | null = null;
  keymap.subscribe(() => {
    cached = null;
    for (const listener of [...listeners]) listener();
  });
  const store: BindingsStore = {
    subscribe: (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    get: () => (cached ??= keymap.bindings()),
  };
  bindingStores.set(keymap, store);
  return store;
}

/** The effective key bindings (preset + overrides + transforms); re-renders on every keymap change. */
export function useKeymapBindings(services: Pick<AppServices, 'keymap'>): readonly KeyBinding[] {
  const { keymap } = services;
  const store = bindingsStore(keymap);
  const subscribe = useCallback((cb: () => void): Unsubscribe => store.subscribe(cb), [store]);
  const get = useCallback((): readonly KeyBinding[] => store.get(), [store]);
  return useSyncExternalStore(subscribe, get, get);
}

/** Re-renders when services are swapped (project opened or closed). */
export function useServicesVersion(services: Pick<AppServices, 'onChange'>): number {
  const { onChange } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => onChange(cb), [onChange]);
  return useVersion(subscribe);
}

/** Whether the user prefers reduced motion (setting first, then the system). */
export function prefersReducedMotion(setting: UserSettings['general']['reducedMotion']): boolean {
  if (setting === 'on') return true;
  if (setting === 'off') return false;
  try {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
